import { requireSignedInClient } from './require-sign-in.js';
import { studioSettings } from '../state/settings.svelte.js';
import { imageReferences } from '../state/image-references.svelte.js';
import { session } from '../state/session.svelte.js';
import { loadImageModelCatalog, getCatalogModel } from '../state/model-catalog.svelte.js';
import { buildModelProjectParams } from './image-model-parameters.svelte.js';
import { reviewSpend } from '../state/spend-review.svelte.js';
import { prepareGenerationRun, generationRun } from '../state/generation-run.svelte.js';
import { createImageRequest, prepareExistingRequest, quoteImageRequest, hasPlanCoverage,
  freezeRequest, entitlementFingerprint, assertRequestCurrent, assertRequestOwner, decimalUnits, unitsToDecimal } from './generation-request.js';
import { modelIsCovered, modelRequiresPremiumSpark, isPartnerModel } from './generation-request.js';
import { confirmQuotedRequest } from './confirm-quoted-request.js';
import { createImageCostEstimator } from './image-cost-estimate.js';
import { takePromptBillingMode } from './prompt-billing-choice.js';
import { estimateExactVideoCost, videoEstimateParameters } from './video-cost-estimate.js';
import { readGenerationCapacity } from './generation-capacity.svelte.js';
import { getVideoAdmission } from './video-capacity.js';
import { quotePromptRequestUnits, rerouteCeilingCandidate, probeRerouteModel, assertRerouteCeilingCurrent } from './prompt-quote.js';

export function generationSourceFingerprint() {
  return JSON.stringify({ phase: session.phase, settings: studioSettings, referenceRevision: imageReferences.revision,
    referenceIds: imageReferences.assets.map(asset => asset.id), guideId: imageReferences.guideImage?.id });
}

/**
 * `buildRequests` is called after the billing context is read, so requests
 * built from live settings (the Mature Content flag) match the source
 * fingerprint frozen beside them. `rerouteCeiling: false` skips the
 * reroute-model price for requests the socket never reroutes (describe-image).
 */
export async function approvePromptRequests(client, buildRequests, {
  accountId = client.account.currentAccount.walletAddress?.toLowerCase(), rerouteCeiling = true
} = {}) {
  let reviewTimeMs = 0;
  let quote = null;
  assertRequestOwner({ accountId }, client.account.currentAccount.walletAddress?.toLowerCase());
  const context = await freshBillingContext(client);
  assertRequestOwner({ accountId }, context.accountId);
  const requests = buildRequests();
  const billingMode = takePromptBillingMode({ ...context, requests });
  const id = crypto.randomUUID();
  const request = freezeRequest({ id, ...context, balance: undefined,
    entitlementFingerprint: entitlementFingerprint(context.subscription),
    requests: requests.map(params => ({ ...params, billingMode })) });
  const slotIds = request.requests.map((_, index) => id + ':' + index);
  // The reroute ceiling mirrors the socket's gate: only a flagged, token-billed
  // request from an account holding Premium Spark can be rerouted, and only
  // while the reroute model has workers, which its live estimate reports (404
  // means none). It is decided afresh on every quote, so the model or the
  // balance appearing between the review and the requote raises QUOTE_CHANGED
  // there, and probed again before each rung so a model that comes online
  // mid-tool stops the call for a fresh review instead of billing above the
  // reviewed amount. A failing estimate fails the quote (prompt-quote.js).
  const estimate = (params) => client.chat.estimateCost(params);
  const candidate = () => rerouteCeiling && billingMode === 'tokens' && rerouteCeilingCandidate({
    requests: request.requests, premiumSpark: client.account.currentAccount.balance.spark.premiumCredit });
  let ceilingApplied = false;
  const quoteRequests = async () => {
    const { total, applied } = await quotePromptRequestUnits(request.requests, {
      estimate, toUnits: decimalUnits, ceiling: candidate()
    });
    ceilingApplied = applied;
    const amount = unitsToDecimal(total);
    return freezeRequest({ kind: 'prompt', accountId: request.accountId, requestId: id, slotIds,
      total: amount, premiumTotal: '0', currency: 'Spark',
      lines: [{ modelName: 'AI prompt tool', count: requests.length, covered: false, amount, premium: false }] });
  };
  try {
    if (billingMode === 'tokens') {
      quote = await quoteRequests();
      await confirmQuotedRequest({ request, quote, context,
        review: details => {
          const startedAt = Date.now();
          return reviewSpend({ ...details, title: 'Review AI prompt tool credits' })
            .finally(() => { reviewTimeMs += Date.now() - startedAt; });
        },
        refreshContext: () => freshBillingContext(client), requote: quoteRequests });
    }
    return { request, quote, requests: request.requests, reviewTimeMs,
      assertCurrent: async () => {
        assertRequestCurrent(request, {
          accountId: client.account.currentAccount.walletAddress.toLowerCase(),
          sourceFingerprint: generationSourceFingerprint(),
          subscription: client.account.currentAccount.subscription
        });
        if (ceilingApplied) return;
        assertRerouteCeilingCurrent({ applied: false, request, quote,
          appliesNow: candidate() && await probeRerouteModel(estimate, request.requests) });
      } };
  } catch (error) {
    error.request = request;
    if (quote && !error.quote) error.quote = quote;
    throw error;
  }
}

export async function freshBillingContext(client, sourceFingerprint = generationSourceFingerprint) {
  const accountId = client.account.currentAccount.walletAddress?.toLowerCase();
  const [subscription] = await Promise.all([
    client.account.getSubscriptionStatus(), client.account.refreshBalance()
  ]);
  requireSignedInClient('Sign in to review this generation.');
  const account = client.account.currentAccount;
  assertRequestOwner({ accountId }, account.walletAddress?.toLowerCase());
  return {
    accountId: account.walletAddress.toLowerCase(),
    sourceFingerprint: sourceFingerprint(),
    subscription,
    balance: { spark: account.balance.spark.net, premiumSpark: account.balance.spark.premiumCredit }
  };
}

export async function approveImageRequest(prompts, options = {}) {
  const client = requireSignedInClient('Sign in to generate images.');
  const accountId = client.account.currentAccount.walletAddress?.toLowerCase();
  await loadImageModelCatalog();
  assertRequestOwner({ accountId }, client.account.currentAccount.walletAddress?.toLowerCase());
  const context = await freshBillingContext(client);
  assertRequestOwner({ accountId }, context.accountId);
  let request;
  let quote;
  try {
    request = options.request
      ? prepareExistingRequest(options.request, context)
      : await createImageRequest({
        prompts,
        modelIds: options.models,
        imagesPerPrompt: options.imagesPerPrompt,
        ...context,
        billingMode: options.billingMode,
        getModel: getCatalogModel,
        resolveModelParams: modelId => buildModelProjectParams(modelId, {
          ...options,
          network: options.network || studioSettings.network,
          modelOverrides: options.modelOverrides?.[modelId] ?? studioSettings.modelOverrides?.[modelId] ?? {},
          disableNSFWFilter: !!studioSettings.matureContentEnabled
        })
      });
    prepareGenerationRun(request);
    if (request.slots.some(slot => slot.projectConfig.billingMode === 'subscription') && !hasPlanCoverage(context.subscription)) {
      const error = new Error('Your plan is no longer active. Review billing before resuming.');
      error.code = 4078;
      throw error;
    }
    quote = await quoteImageRequest(request, createImageCostEstimator(client, context.subscription));
    prepareGenerationRun(request, quote);
    await confirmQuotedRequest({
      request, quote, context,
      review: details => reviewSpend({ ...details, title: 'Review this image batch' }),
      refreshContext: () => freshBillingContext(client),
      requote: () => quoteImageRequest(request, createImageCostEstimator(client, context.subscription))
    });
    return request;
  } catch (error) {
    if (request) error.request = request;
    if (quote && !error.quote) error.quote = quote;
    generationRun.status = error.code === 'COST_APPROVAL_CANCELED' ? 'idle' : 'error';
    throw error;
  }
}

// Called after the original-image fit so approval and submission share the
// same source bytes, dimensions, frames and billing intent.
export async function approveVideoProject(params, { accountId }) {
  const client = requireSignedInClient('Sign in to animate this image.');
  assertRequestOwner({ accountId }, client.account.currentAccount.walletAddress?.toLowerCase());
  const snapshot = await client.apiClient.rest.get('/v1/model-catalog?mediaType=video&include=parameters');
  const model = snapshot.data.models.find(entry => entry.id === params.modelId);
  if (!model) throw new Error('The selected video model is missing from the active catalog.');
  const context = await freshBillingContext(client);
  assertRequestOwner({ accountId }, context.accountId);
  const covered = params.billingMode !== 'tokens' && modelIsCovered(model, context.subscription);
  const projectConfig = { ...params, network: params.network || 'fast',
    billingMode: covered ? 'subscription' : 'tokens' };
  const id = crypto.randomUUID();
  const request = freezeRequest({ id, kind: 'video', ...context, balance: undefined,
    entitlementFingerprint: entitlementFingerprint(context.subscription), projectConfig });
  const estimate = videoEstimateParameters(projectConfig);
  const requote = async () => {
    const cost = await estimateExactVideoCost(client, estimate, model, context.subscription);
    const amount = covered ? '0' : unitsToDecimal(decimalUnits(cost.spark));
    return freezeRequest({ requestId: id, accountId: request.accountId, kind: 'video', slotIds: [id],
      total: amount, premiumTotal: modelRequiresPremiumSpark(model) ? amount : '0', currency: 'Spark',
      lines: [{ modelName: model.name, count: 1, covered, amount,
        premium: modelRequiresPremiumSpark(model), partner: isPartnerModel(model),
        discountBpsApplied: cost.discountBpsApplied,
        settings: { ...estimate, durationSeconds: projectConfig.modelId.startsWith('minimax-h3-')
          ? estimate.frames / estimate.fps : estimate.duration,
          generateAudio: projectConfig.generateAudio,
          outputWidth: projectConfig.width * (projectConfig.modelId.endsWith('_2stage') ? 2 : 1),
          outputHeight: projectConfig.height * (projectConfig.modelId.endsWith('_2stage') ? 2 : 1),
          seed: projectConfig.seed } }] });
  };
  try {
    const quote = await requote();
    await confirmQuotedRequest({ request, quote, context,
      review: details => reviewSpend({ ...details, title: 'Review this video' }),
      refreshContext: () => freshBillingContext(client), requote });
    const capacity = await readGenerationCapacity(request);
    const admission = getVideoAdmission(capacity, projectConfig, model, context.balance);
    if (!admission.canSubmit) throw Object.assign(new Error(admission.message), {
      code: admission.status === 'full' ? 'ACCOUNT_QUEUE_FULL' : 'CAPACITY_UNAVAILABLE' });
    return request;
  } catch (error) {
    error.request = request;
    throw error;
  }
}
